import {
  ApiFn,
  BaseReq,
  FailedResp,
  PaginatedResp,
  Resp,
} from '@paradigm/api/src/fetch-api';
import createObservable, {
  Observable,
  ReadonlyObservable,
} from '@paradigm/utils/src/createObservable';
import { exceptionLogger } from '@paradigm/logging';
import { ProductCode } from '#/products/types';
import EntityStore, { BaseEntity } from '../store/entity';

type Cursor = string | null;
export type Status = 'fetching-latest' | 'fetching-more' | 'idle';
export type ErrorHandler = (error: FailedResp) => void;

export interface ListReq extends BaseReq {
  readonly cursor?: string | null;
  readonly pageSize?: number;
  strategyCodes?: string[];
  productCodes?: ProductCode[] | string[];
}

export interface FetchData {
  readonly status: Status;
  readonly hasMore: boolean;
  readonly canFetchMore: boolean;
}

type MaybeResp = Resp<PaginatedResp> | null;

interface ListFetcherDeps<Req extends ListReq, Entity extends BaseEntity> {
  readonly fetchers: readonly ApiFn<Req, PaginatedResp>[];
  readonly store: EntityStore<Entity>;
  readonly filterFn?: (e: Entity) => boolean;
  readonly pageSize: number;
}

const INITIAL_FETCH_DATA: FetchData = {
  hasMore: false,
  canFetchMore: true,
  status: 'idle',
};

/**
 * This class receives a list of fetchers that inserts data to the same store
 * and returns a paginated collection of the inserted data, given the current page.
 *
 * The data is going to be sorted given the criteria defined in the store
 *
 * @param fetchers List of api functions that handles paginated data
 * @param store Store that contains data
 * @param pageSize Each page size
 *  */
export class MergedSourcesFetcher<
  Req extends ListReq,
  Entity extends BaseEntity,
> {
  private cursors: Cursor[] = [];
  private fetchDataObservable: Observable<FetchData>;
  readonly fetchData: ReadonlyObservable<FetchData>;
  private currentPage: Observable<number>;

  constructor(private deps: ListFetcherDeps<Req, Entity>) {
    this.fetchDataObservable = createObservable<FetchData>(
      'merged-sources-fetcher:fetch-data',
      INITIAL_FETCH_DATA,
    );
    this.currentPage = createObservable<number>(
      'merged-sources-fetcher:current-page',
      1,
    );
    this.fetchData = this.fetchDataObservable;
    this.setupInitialState();
  }

  selectPaginatedIds(): readonly Entity['id'][] {
    const { pageSize } = this.deps;
    const paginatedIds = this.filteredIds().slice(
      0,
      pageSize * this.currentPage.get(),
    );

    return paginatedIds;
  }

  subscribePaginatedIds = (cb: (ids: readonly Entity['id'][]) => void) => {
    const { pageSize, store } = this.deps;
    const paginatedHandler = () => {
      const paginatedIds = this.filteredIds().slice(
        0,
        pageSize * this.currentPage.get(),
      );
      cb(paginatedIds);
    };

    const unsubPage = this.currentPage.observe(paginatedHandler);
    const unsubsListIds = store.subscribeList(paginatedHandler);

    return () => {
      unsubPage();
      unsubsListIds();
    };
  };

  public getFetchers(): readonly ApiFn<Req, PaginatedResp>[] {
    return this.deps.fetchers;
  }

  fetchLatest = (req: Req, onError: ErrorHandler) => {
    const { pageSize, fetchers } = this.deps;
    this.setStatus('fetching-latest');

    const fetchPromises = fetchers.map(async (fetcher) =>
      fetcher({ ...req, pageSize }),
    );

    Promise.all(fetchPromises)
      .then((resps) => {
        const errorResp = resps.find((resp): resp is FailedResp => !resp.ok);
        if (errorResp != null) {
          onError(errorResp);
          this.setupInitialState();
          return;
        }

        this.updateCursors(resps);

        this.currentPage.set(1);
        this.setStatus('idle');
      })
      .catch(exceptionLogger('Unexpected failure fetching latest entities'));
  };

  fetchMore = (req: Req, onError: ErrorHandler) => {
    const { pageSize, fetchers } = this.deps;
    this.setStatus('fetching-more');

    const fetchPromises = fetchers.map(async (fetcher, index) => {
      if (this.cursors[index] == null) return null;

      return fetcher({ ...req, cursor: this.cursors[index], pageSize });
    });

    Promise.all(fetchPromises)
      .then((resps) => {
        const errorResp = resps.find(
          (resp): resp is FailedResp => resp?.ok === false,
        );
        if (errorResp != null) {
          onError(errorResp);
          this.setupInitialState();
          return;
        }

        this.updateCursors(resps);

        this.currentPage.set(this.currentPage.get() + 1);
        this.setStatus('idle');
      })
      .catch(exceptionLogger('Unexpected failure fetching more entities'));
  };

  private filteredIds() {
    const { store, filterFn } = this.deps;
    const ids = store.selectAllIds();
    if (filterFn == null) return ids;

    return ids.filter((id) => {
      const entity = store.selectById(id);
      if (entity == null) return false;

      return filterFn(entity);
    });
  }

  private updateCursors(resps: MaybeResp[]) {
    resps.forEach((resp, index) => {
      if (resp?.ok === true) {
        this.cursors[index] = resp.data.next;
      }
    });
  }

  private setupInitialState() {
    const { fetchers, store } = this.deps;
    this.currentPage.set(1);
    store.clear();
    this.cursors = fetchers.map(() => null);
    this.fetchDataObservable.set(INITIAL_FETCH_DATA);
  }

  private setStatus(status: Status) {
    const { pageSize } = this.deps;
    const allIds = this.filteredIds();
    const totalPages = Math.ceil(allIds.length / pageSize);

    const hasMoreToDisplay = this.currentPage.get() < totalPages;
    const hasMoreToFetch = this.cursors.some((cursor) => cursor != null);
    const hasMore = hasMoreToFetch || hasMoreToDisplay;

    const fetchData = {
      hasMore,
      canFetchMore: hasMore && status === 'idle',
      status,
    };

    this.fetchDataObservable.set(fetchData);
  }
}
